import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { FieldArray, FormikProvider, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  Col,
  Container,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import SettingApi from "api/settingApi";
import BreadCrumb from "components/Common/BreadCrumb";
import * as Yup from "yup";
import TextArea from "antd/es/input/TextArea";
import { NumericFormat } from "react-number-format";
import { Collapse } from "antd";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import { LIST_OPTION_AI_SETTING_TARGET_RANGE } from "helpers/constans";
import ModalConfirm from "components/Common/ModalConfirm";

registerLocale("en", en);
registerLocale("ko", ko);

const DEFAULT_CATEGORY_PROMPTS = {
  category: "",
  prompt: "",
};

const AISetting = () => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [checkedTab, setCheckedTab] = useState<number>(1);
  const [activeCommunityKeys, setActiveCommunityKeys] = useState<number[]>([0]);
  const [activeCommentKeys, setActiveCommentKeys] = useState<number[]>([0]);
  const [activeReviewKeys, setActiveReviewKeys] = useState<number[]>([0]);
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);
  const [indexDelete, setIndexDelete] = useState<any>(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [formRemove, setFormRemove] = useState<any>(null);

  const onCloseConfirmClick = () => {
    setIsOpenConfirm(false);
    setIndexDelete(null);
    setIsConfirmLoading((_prev) => false);
    setFormRemove(null);
  };

  const handleRemoveItem = (index: number) => {
    if (!formRemove) return;

    const updatedItems = formRemove?.values?.categoryPrompts.filter(
      (_: any, i: any) => i !== index
    );
    formRemove.setFieldValue("categoryPrompts", updatedItems);
  };

  const handleActionDelete = async () => {
    try {
      handleRemoveItem(indexDelete);
      setIsOpenConfirm(false);
      toast(
        `${t("The process has been completed.")}`,
        CONFIG_OPTION_TOAST_NORMAL
      );
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      setIndexDelete(null);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
    setIsConfirmLoading((_prev) => false);
  };

  const handleCreateUpdateAISetting = async (id: any, data: any) => {
    try {
      const response: any = id
        ? await SettingApi.putAISetting(id, data)
        : await SettingApi.postAISetting(data);

      if (response?.code === 200) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsLoading((_prev) => false);
        if (response.success == false) {
          toast(`${response?.message}`, CONFIG_OPTION_TOAST_ERROR);
        }
      }
    } catch (error: any) {
      toast(`${error}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const handleSubmitCommunity = async (values: any) => {
    if (!formik?.isValid) return;
    setIsLoading(true);
    const delayRangeFormat =
      values?.communityDelayRange?.start && values?.communityDelayRange?.end
        ? `[${values?.communityDelayRange?.start}, ${values?.communityDelayRange?.end}]`
        : "";

    const formatedData = {
      name: values?.name || "Community Setting",
      type: "community",
      aiModel: values?.aiModel,
      targetRange: values?.targetRange || 7,
      generationInterval: values?.generationInterval,
      delayRange: delayRangeFormat,
      tbedPrompts: [values?.tbedPrompts || ""],
      categoryPrompts: values?.categoryPrompts,
    };

    handleCreateUpdateAISetting(values?.id, formatedData);
  };

  const validationSchema = Yup.object({
    aiModel: Yup.string().required(t("This field is required")),
  });

  const formik = useFormik({
    initialValues: {
      aiModel: "gemma3:12b",
      generationInterval: "",
      communityDelayRange: {
        start: 30,
        end: 180,
      },
      tbedPrompts: "",
      categoryPrompts: [DEFAULT_CATEGORY_PROMPTS] as any[],
    },
    validationSchema,
    onSubmit: handleSubmitCommunity,
  });

  const handleSubmitComment = async (values: any) => {
    if (!formikComment?.isValid) return;
    setIsLoading(true);
    const delayRangeFormat =
      values?.commentDelayRange?.start && values?.commentDelayRange?.end
        ? `[${values?.commentDelayRange?.start}, ${values?.commentDelayRange?.end}]`
        : "";

    const formatedData = {
      name: values?.name || "Comment Setting",
      type: "comment",
      aiModel: values?.aiModel,
      generationInterval: values?.generationInterval,
      targetRange: values?.targetRange,
      delayRange: delayRangeFormat,
      generationPerFeed: values?.generationPerFeed,
      maxPerDay: values?.maxPerDay,
      maxPerRun: values?.maxPerRun,
      categoryPrompts: values?.categoryPrompts,
    };

    handleCreateUpdateAISetting(values?.id, formatedData);
  };

  const validationSchemaComment = Yup.object({
    aiModel: Yup.string().required(t("This field is required")),
  });

  const formikComment = useFormik({
    initialValues: {
      aiModel: "",
      generationInterval: "",
      targetRange: 7,
      generationPerFeed: "",
      commentDelayRange: {
        start: 0,
        end: 0,
      },
      maxPerDay: "",
      maxPerRun: "",
      categoryPrompts: [DEFAULT_CATEGORY_PROMPTS] as any[],
    },
    validationSchema: validationSchemaComment,
    onSubmit: handleSubmitComment,
  });

  const handleSubmitReview = async (values: any) => {
    if (!formikReview?.isValid) return;
    setIsLoading(true);
    const delayRangeFormat =
      values?.reviewDelayRange?.start && values?.reviewDelayRange?.end
        ? `[${values?.reviewDelayRange?.start}, ${values?.reviewDelayRange?.end}]`
        : "";

    const formatedData = {
      name: values?.name || "Review Setting",
      type: "review",
      aiModel: values?.aiModel,
      generationInterval: values?.generationInterval,
      targetRange: values?.targetRange,
      delayRange: delayRangeFormat,
      generationPerFeed: values?.generationPerFeed,
      maxPerDay: values?.maxPerDay,
      maxPerRun: values?.maxPerRun,
      categoryPrompts: values?.categoryPrompts,
    };

    handleCreateUpdateAISetting(values?.id, formatedData);
  };

  const validationSchemaReview = Yup.object({
    aiModel: Yup.string().required(t("This field is required")),
  });

  const formikReview = useFormik({
    initialValues: {
      aiModel: "",
      generationInterval: "",
      targetRange: "",
      generationPerFeed: "",
      reviewDelayRange: {
        start: 0,
        end: 0,
      },
      maxPerDay: "",
      maxPerRun: "",
      categoryPrompts: [DEFAULT_CATEGORY_PROMPTS] as any[],
    },
    validationSchema: validationSchemaReview,
    onSubmit: handleSubmitReview,
  });

  const handleData = async () => {
    setIsLoading((_prev) => true);
    try {
      const response: any = await SettingApi.getAISetting();
      if (response?.data) {
        const dataCommunity = response?.data?.find(
          (item: any) => item?.type === "community"
        );
        const dataComment = response?.data?.find(
          (item: any) => item?.type === "comment"
        );
        const dataReview = response?.data?.find(
          (item: any) => item?.type === "review"
        );
        if (dataCommunity) {
          setActiveCommunityKeys(
            dataCommunity?.categoryPrompts?.map(
              (_: any, index: number) => index
            )
          );
          const delayRangeString = dataCommunity?.delayRange || "";
          const [start, end] = JSON.parse(delayRangeString);

          formik?.setValues({
            ...dataCommunity,
            tbedPrompts: dataCommunity?.tbedPrompts?.[0] || "",
            communityDelayRange: {
              start: start,
              end: end,
            },
          });
        }

        if (dataComment) {
          setActiveCommentKeys(
            dataComment?.categoryPrompts?.map((_: any, index: number) => index)
          );
          const delayRangeString = dataComment?.delayRange || "";
          const [start, end] = JSON.parse(delayRangeString);
          formikComment?.setValues({
            ...dataComment,
            commentDelayRange: {
              start: start,
              end: end,
            },
          });
        }

        if (dataReview) {
          setActiveReviewKeys(
            dataReview?.categoryPrompts?.map((_: any, index: number) => index)
          );
          const delayRangeString = dataReview?.delayRange || "";
          const [start, end] = JSON.parse(delayRangeString);
          formikReview?.setValues({
            ...dataReview,
            reviewDelayRange: {
              start: start,
              end: end,
            },
          });
        }
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setIsLoading((_prev) => false);
    }
  };

  useEffect(() => {
    handleData();
  }, []);

  useEffect(() => {
    document.title = `${t("AI Setting")} - ${t("Setting Manager")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content ai-setting">
        <Container fluid>
          <BreadCrumb title={t("AI Setting")} pageTitle={t("Home")} />

          <Card
            id="AI_SETTING"
            style={{
              boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)",
              borderTop: "unset",
            }}
          >
            <div
              style={{
                background: "#f3f6f9",
                borderTopLeftRadius: "0.6rem",
                borderTopRightRadius: "0.6rem",
                position: "sticky",
                top: "0px",
                zIndex: 99,
                borderTop: "1px solid #e9ebec",
              }}
            >
              <div
                className="d-block"
                style={{
                  background: "#f3f6f9",
                  borderRadius: "0.6rem",
                  width: "fit-content",
                }}
              >
                <Nav
                  tabs
                  className="nav nav-tabs nav-link-none-hover nav-justified"
                >
                  <NavItem style={{ minWidth: "200px" }}>
                    <NavLink
                      style={{
                        cursor: "pointer",
                        background: `${checkedTab === 1 ? "var(--vz-primary)" : "unset"
                          }`,
                        color: `${checkedTab === 1 ? "white" : "unset"}`,
                        borderTopRightRadius: 0,
                      }}
                      onClick={() => setCheckedTab(1)}
                    >
                      {t("Community Setting")}
                    </NavLink>
                  </NavItem>
                  <NavItem style={{ minWidth: "200px" }}>
                    <NavLink
                      style={{
                        cursor: `pointer`,
                        background: `${checkedTab === 2 ? "var(--vz-primary)" : "unset"
                          }`,
                        color: `${checkedTab === 2 ? "white" : "unset"}`,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                      }}
                      onClick={() => {
                        setCheckedTab(2);
                      }}
                    >
                      {t("Comment Setting")}
                    </NavLink>
                  </NavItem>
                  <NavItem style={{ minWidth: "200px" }}>
                    <NavLink
                      style={{
                        cursor: `pointer`,
                        background: `${checkedTab === 3 ? "var(--vz-primary)" : "unset"
                          }`,
                        color: `${checkedTab === 3 ? "white" : "unset"}`,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                      }}
                      onClick={() => {
                        setCheckedTab(3);
                      }}
                    >
                      {t("Review Setting")}
                    </NavLink>
                  </NavItem>
                </Nav>
              </div>
            </div>
            <TabContent activeTab={checkedTab}>
              <TabPane tabId={1}>
                {checkedTab === 1 && (
                  <form onSubmit={formik.handleSubmit}>
                    <Row className="mt-3 px-3">
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="aiModel" className="form-label">
                            {t("AI Model")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="aiModel"
                            type="text"
                            className="form-control"
                            id="aiModel"
                            placeholder={t("AI Model")}
                            value={formik?.values?.aiModel}
                            onChange={(event: any) => {
                              formik.setFieldValue(
                                "aiModel",
                                event?.target?.value
                              );
                            }}
                          />
                          {formik?.touched?.aiModel &&
                            formik?.errors?.aiModel ? (
                            <div className="text-danger mt-2">
                              {formik?.errors?.aiModel}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label
                            htmlFor="generationInterval"
                            className="form-label"
                          >
                            {t("Post Generation Interval")}{" "}
                          </Label>
                          <NumericFormat
                            className="form-control"
                            value={formik?.values?.generationInterval}
                            placeholder={t("Post Generation Interval")}
                            allowLeadingZeros
                            thousandSeparator=","
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              formik.setFieldValue(
                                "generationInterval",
                                floatValue
                              );
                            }}
                          />
                          {formik?.touched?.generationInterval &&
                            formik?.errors?.generationInterval ? (
                            <div className="text-danger mt-2">
                              {formik?.errors?.generationInterval}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label
                            htmlFor="communityDelayRange"
                            className="form-label"
                          >
                            {t("Post Delay Range")}{" "}
                          </Label>

                          <Row>
                            <Col lg={6}>
                              <NumericFormat
                                className="form-control"
                                value={
                                  formik?.values?.communityDelayRange?.start
                                }
                                placeholder={t("Post Delay Range")}
                                allowLeadingZeros
                                thousandSeparator=","
                                onValueChange={(values: any) => {
                                  const { floatValue } = values;
                                  formik.setFieldValue(
                                    "communityDelayRange.start",
                                    floatValue
                                  );
                                }}
                              />
                              {formik?.touched?.communityDelayRange?.start &&
                                formik?.errors?.communityDelayRange?.start ? (
                                <div className="text-danger mt-2">
                                  {formik?.errors?.communityDelayRange?.start}
                                </div>
                              ) : null}
                            </Col>
                            <Col lg={6}>
                              <NumericFormat
                                className="form-control"
                                value={formik?.values?.communityDelayRange?.end}
                                placeholder={t("Post Delay Range")}
                                allowLeadingZeros
                                thousandSeparator=","
                                onValueChange={(values: any) => {
                                  const { floatValue } = values;
                                  formik.setFieldValue(
                                    "communityDelayRange.end",
                                    floatValue
                                  );
                                }}
                              />
                              {formik?.touched?.communityDelayRange?.end &&
                                formik?.errors?.communityDelayRange?.end ? (
                                <div className="text-danger mt-2">
                                  {formik?.errors?.communityDelayRange?.end}
                                </div>
                              ) : null}
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="mb-3">
                          <Label htmlFor="contactEmail" className="form-label">
                            {t("Category Prompts")}{" "}
                            <span className="text-danger">*</span>
                          </Label>

                          <div>
                            <FormikProvider value={formik}>
                              <FieldArray
                                name="categoryPrompts"
                                render={({ push }) => (
                                  <>
                                    <Collapse
                                      activeKey={activeCommunityKeys}
                                      onChange={(keys) =>
                                        setActiveCommunityKeys(
                                          Array.isArray(keys)
                                            ? keys.map(Number)
                                            : []
                                        )
                                      }
                                      collapsible="icon"
                                      items={formik.values.categoryPrompts.map(
                                        (prompt, index) => ({
                                          key: index,
                                          label: (
                                            <div className="">
                                              <Input
                                                name={`categoryPrompts.${index}.category`}
                                                value={prompt.category ?? ""}
                                                placeholder={`${t(
                                                  "Enter category"
                                                )}...`}
                                                onChange={formik.handleChange}
                                              />
                                              {index > 0 && (
                                                <small
                                                  style={{
                                                    right: 4,
                                                    top: "calc(50% - 11px)",
                                                    fontSize: 14,
                                                  }}
                                                  className="text-danger position-absolute cursor-pointer"
                                                  onClick={() => {
                                                    setIsOpenConfirm(true);
                                                    setIndexDelete(index);
                                                    setFormRemove(formik);
                                                  }}
                                                >
                                                  <i className="ri-delete-bin-3-fill align-bottom"></i>
                                                </small>
                                              )}
                                            </div>
                                          ),
                                          children: (
                                            <TextArea
                                              name={`categoryPrompts.${index}.prompt`}
                                              value={prompt.prompt ?? ""}
                                              autoSize={{ minRows: 4 }}
                                              placeholder={`${t(
                                                "Enter prompt"
                                              )}...`}
                                              onChange={formik.handleChange}
                                            />
                                          ),
                                        })
                                      )}
                                    />

                                    <button
                                      className="btn btn-dark fs-14 mt-4"
                                      type="button"
                                      style={{ width: "140px" }}
                                      onClick={() => {
                                        const newIndex =
                                          formik.values.categoryPrompts.length;
                                        push(DEFAULT_CATEGORY_PROMPTS);
                                        setActiveCommunityKeys((prev) => [
                                          ...prev,
                                          newIndex,
                                        ]);
                                      }}
                                    >
                                      {t("Add Prompt")}
                                    </button>
                                  </>
                                )}
                              />
                            </FormikProvider>
                          </div>
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="mb-3">
                          <Label htmlFor="tbedPrompts" className="form-label">
                            {t("TBED Prompts")}{" "}
                            {/* <span className="text-danger">*</span> */}
                          </Label>

                          <div>
                            <TextArea
                              name={`tbedPrompts`}
                              value={formik?.values?.tbedPrompts ?? ""}
                              autoSize={{ minRows: 4 }}
                              placeholder={`${t(
                                "Enter TBED Prompts"
                              )}...`}
                              onChange={formik.handleChange}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="hstack gap-2 justify-content-end my-4">
                          <button
                            className="btn btn-primary fs-14"
                            color="success"
                            type="submit"
                            disabled={isLoading}
                            style={{ width: "140px" }}
                          >
                            {isLoading ? (
                              <Spinner size="sm" className="me-2"></Spinner>
                            ) : (
                              <>{t("Update")}</>
                            )}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </form>
                )}
              </TabPane>
              <TabPane tabId={2}>
                {checkedTab === 2 && (
                  <form onSubmit={formikComment.handleSubmit}>
                    <Row className="mt-3 px-3">
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="aiModel" className="form-label">
                            {t("AI Model")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="aiModel"
                            type="text"
                            className="form-control"
                            id="aiModel"
                            placeholder={t("AI Model")}
                            value={formikComment?.values?.aiModel}
                            onChange={(event: any) => {
                              formikComment.setFieldValue(
                                "aiModel",
                                event?.target?.value
                              );
                            }}
                          />
                          {formikComment?.touched?.aiModel &&
                            formikComment?.errors?.aiModel ? (
                            <div className="text-danger mt-2">
                              {formikComment?.errors?.aiModel}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label
                            htmlFor="generationInterval"
                            className="form-label"
                          >
                            {t("Comment Generation Interval")}{" "}
                          </Label>
                          <NumericFormat
                            className="form-control"
                            value={formikComment?.values?.generationInterval}
                            placeholder={t("Comment Generation Interval")}
                            allowLeadingZeros
                            thousandSeparator=","
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              formikComment.setFieldValue(
                                "generationInterval",
                                floatValue
                              );
                            }}
                          />
                          {formikComment?.touched?.generationInterval &&
                            formikComment?.errors?.generationInterval ? (
                            <div className="text-danger mt-2">
                              {formikComment?.errors?.generationInterval}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col md={6} lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="targetRange" className="form-label">
                            {t("Target Range")}
                          </Label>
                          <DropdownOptionNew
                            name="status"
                            dataList={LIST_OPTION_AI_SETTING_TARGET_RANGE || []}
                            placeholder={`${t("Target Range")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={formikComment?.values?.targetRange}
                            onChangeSelect={(e: any) =>
                              formikComment.setFieldValue(
                                "targetRange",
                                e?.value
                              )
                            }
                            isHasOptionAll={false}
                          />
                          {formikComment.touched.targetRange &&
                            formikComment.errors.targetRange ? (
                            <div className="text-danger mt-2">
                              {formikComment.errors.targetRange}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label
                            htmlFor="generationPerFeed"
                            className="form-label"
                          >
                            {t("Comment per Feed")}{" "}
                          </Label>
                          <NumericFormat
                            className="form-control"
                            value={formikComment?.values?.generationPerFeed}
                            placeholder={t("Comment per Feed")}
                            allowLeadingZeros
                            thousandSeparator=","
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              formikComment.setFieldValue(
                                "generationPerFeed",
                                floatValue
                              );
                            }}
                          />
                          {formikComment?.touched?.generationPerFeed &&
                            formikComment?.errors?.generationPerFeed ? (
                            <div className="text-danger mt-2">
                              {formikComment?.errors?.generationPerFeed}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label
                            htmlFor="commentDelayRange"
                            className="form-label"
                          >
                            {t("Comment Delay Range")}{" "}
                          </Label>

                          <Row>
                            <Col lg={6}>
                              <NumericFormat
                                className="form-control"
                                value={
                                  formikComment?.values?.commentDelayRange
                                    ?.start
                                }
                                placeholder={t("Comment Delay Range")}
                                allowLeadingZeros
                                thousandSeparator=","
                                onValueChange={(values: any) => {
                                  const { floatValue } = values;
                                  formikComment.setFieldValue(
                                    "commentDelayRange.start",
                                    floatValue
                                  );
                                }}
                              />
                              {formikComment?.touched?.commentDelayRange
                                ?.start &&
                                formikComment?.errors?.commentDelayRange
                                  ?.start ? (
                                <div className="text-danger mt-2">
                                  {
                                    formikComment?.errors?.commentDelayRange
                                      ?.start
                                  }
                                </div>
                              ) : null}
                            </Col>
                            <Col lg={6}>
                              <NumericFormat
                                className="form-control"
                                value={
                                  formikComment?.values?.commentDelayRange?.end
                                }
                                placeholder={t("Post Delay Range")}
                                allowLeadingZeros
                                thousandSeparator=","
                                onValueChange={(values: any) => {
                                  const { floatValue } = values;
                                  formikComment.setFieldValue(
                                    "commentDelayRange.end",
                                    floatValue
                                  );
                                }}
                              />
                              {formikComment?.touched?.commentDelayRange?.end &&
                                formikComment?.errors?.commentDelayRange?.end ? (
                                <div className="text-danger mt-2">
                                  {
                                    formikComment?.errors?.commentDelayRange
                                      ?.end
                                  }
                                </div>
                              ) : null}
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="maxPerDay" className="form-label">
                            {t("Max Comments per Day")}{" "}
                          </Label>
                          <NumericFormat
                            className="form-control"
                            value={formikComment?.values?.maxPerDay}
                            placeholder={t("Max Comments per Day")}
                            allowLeadingZeros
                            thousandSeparator=","
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              formikComment.setFieldValue(
                                "maxPerDay",
                                floatValue
                              );
                            }}
                          />
                          {formikComment?.touched?.maxPerDay &&
                            formikComment?.errors?.maxPerDay ? (
                            <div className="text-danger mt-2">
                              {formikComment?.errors?.maxPerDay}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="maxPerRun" className="form-label">
                            {t("Max Comments per Run")}{" "}
                          </Label>
                          <NumericFormat
                            className="form-control"
                            value={formikComment?.values?.maxPerRun}
                            placeholder={t("Max Comments per Run")}
                            allowLeadingZeros
                            thousandSeparator=","
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              formikComment.setFieldValue(
                                "maxPerRun",
                                floatValue
                              );
                            }}
                          />
                          {formikComment?.touched?.maxPerRun &&
                            formikComment?.errors?.maxPerRun ? (
                            <div className="text-danger mt-2">
                              {formikComment?.errors?.maxPerRun}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="mb-3">
                          <Label htmlFor="contactEmail" className="form-label">
                            {t("Category Prompts")}{" "}
                            <span className="text-danger">*</span>
                          </Label>

                          <div>
                            <FormikProvider value={formikComment}>
                              <FieldArray
                                name="categoryPrompts"
                                render={({ push }) => (
                                  <>
                                    <Collapse
                                      activeKey={activeCommentKeys}
                                      onChange={(keys) =>
                                        setActiveCommentKeys(
                                          Array.isArray(keys)
                                            ? keys.map(Number)
                                            : []
                                        )
                                      }
                                      collapsible="icon"
                                      items={formikComment?.values?.categoryPrompts?.map(
                                        (prompt, index) => ({
                                          key: index,
                                          label: (
                                            <div className="">
                                              <Input
                                                name={`categoryPrompts.${index}.category`}
                                                value={prompt.category ?? ""}
                                                placeholder={`${t(
                                                  "Enter category"
                                                )}...`}
                                                onChange={
                                                  formikComment.handleChange
                                                }
                                              />
                                              {index > 0 && (
                                                <small
                                                  style={{
                                                    right: 4,
                                                    top: "calc(50% - 11px)",
                                                    fontSize: 14,
                                                  }}
                                                  className="text-danger position-absolute cursor-pointer"
                                                  onClick={() => {
                                                    setIsOpenConfirm(true);
                                                    setIndexDelete(index);
                                                    setFormRemove(
                                                      formikComment
                                                    );
                                                  }}
                                                >
                                                  <i className="ri-delete-bin-3-fill align-bottom"></i>
                                                </small>
                                              )}
                                            </div>
                                          ),
                                          children: (
                                            <TextArea
                                              name={`categoryPrompts.${index}.prompt`}
                                              value={prompt.prompt ?? ""}
                                              autoSize={{ minRows: 6 }}
                                              placeholder={`${t(
                                                "Enter prompt"
                                              )}...`}
                                              onChange={
                                                formikComment.handleChange
                                              }
                                            />
                                          ),
                                        })
                                      )}
                                    />

                                    <button
                                      className="btn btn-dark fs-14 mt-4"
                                      type="button"
                                      style={{ width: "140px" }}
                                      onClick={() => {
                                        const newIndex =
                                          formikComment.values.categoryPrompts
                                            .length;
                                        push(DEFAULT_CATEGORY_PROMPTS);
                                        setActiveCommentKeys((prev) => [
                                          ...prev,
                                          newIndex,
                                        ]);
                                      }}
                                    >
                                      {t("Add Prompt")}
                                    </button>
                                  </>
                                )}
                              />
                            </FormikProvider>
                          </div>
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="hstack gap-2 justify-content-end my-4">
                          <button
                            className="btn btn-primary fs-14"
                            color="success"
                            type="submit"
                            disabled={isLoading}
                            style={{ width: "140px" }}
                          >
                            {isLoading ? (
                              <Spinner size="sm" className="me-2"></Spinner>
                            ) : (
                              <>{t("Update")}</>
                            )}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </form>
                )}
              </TabPane>
              <TabPane tabId={3}>
                {checkedTab === 3 && (
                  <form onSubmit={formikReview.handleSubmit}>
                    <Row className="mt-3 px-3">
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="aiModel" className="form-label">
                            {t("AI Model")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="aiModel"
                            type="text"
                            className="form-control"
                            id="aiModel"
                            placeholder={t("AI Model")}
                            value={formikReview?.values?.aiModel}
                            onChange={(event: any) => {
                              formikReview.setFieldValue(
                                "aiModel",
                                event?.target?.value
                              );
                            }}
                          />
                          {formikReview?.touched?.aiModel &&
                            formikReview?.errors?.aiModel ? (
                            <div className="text-danger mt-2">
                              {formikReview?.errors?.aiModel}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label
                            htmlFor="generationInterval"
                            className="form-label"
                          >
                            {t("Review Generation Interval")}{" "}
                          </Label>
                          <NumericFormat
                            className="form-control"
                            value={formikReview?.values?.generationInterval}
                            placeholder={t("Review Generation Interval")}
                            allowLeadingZeros
                            thousandSeparator=","
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              formikReview.setFieldValue(
                                "generationInterval",
                                floatValue
                              );
                            }}
                          />
                          {formikReview?.touched?.generationInterval &&
                            formikReview?.errors?.generationInterval ? (
                            <div className="text-danger mt-2">
                              {formikReview?.errors?.generationInterval}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col md={6} lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="targetRange" className="form-label">
                            {t("Target Range")}
                          </Label>
                          <DropdownOptionNew
                            name="status"
                            dataList={LIST_OPTION_AI_SETTING_TARGET_RANGE || []}
                            placeholder={`${t("Target Range")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={formikReview?.values?.targetRange}
                            onChangeSelect={(e: any) =>
                              formikReview.setFieldValue(
                                "targetRange",
                                e?.value
                              )
                            }
                            isHasOptionAll={false}
                          />
                          {formikReview.touched.targetRange &&
                            formikReview.errors.targetRange ? (
                            <div className="text-danger mt-2">
                              {formikReview.errors.targetRange}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label
                            htmlFor="generationPerFeed"
                            className="form-label"
                          >
                            {t("Review per Feed")}{" "}
                          </Label>
                          <NumericFormat
                            className="form-control"
                            value={formikReview?.values?.generationPerFeed}
                            placeholder={t("Review per Feed")}
                            allowLeadingZeros
                            thousandSeparator=","
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              formikReview.setFieldValue(
                                "generationPerFeed",
                                floatValue
                              );
                            }}
                          />
                          {formikReview?.touched?.generationPerFeed &&
                            formikReview?.errors?.generationPerFeed ? (
                            <div className="text-danger mt-2">
                              {formikReview?.errors?.generationPerFeed}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label
                            htmlFor="reviewDelayRange"
                            className="form-label"
                          >
                            {t("Review Delay Range")}{" "}
                          </Label>

                          <Row>
                            <Col lg={6}>
                              <NumericFormat
                                className="form-control"
                                value={
                                  formikReview?.values?.reviewDelayRange?.start
                                }
                                placeholder={t("Review Delay Range")}
                                allowLeadingZeros
                                thousandSeparator=","
                                onValueChange={(values: any) => {
                                  const { floatValue } = values;
                                  formikReview.setFieldValue(
                                    "reviewDelayRange.start",
                                    floatValue
                                  );
                                }}
                              />
                              {formikReview?.touched?.reviewDelayRange?.start &&
                                formikReview?.errors?.reviewDelayRange?.start ? (
                                <div className="text-danger mt-2">
                                  {
                                    formikReview?.errors?.reviewDelayRange
                                      ?.start
                                  }
                                </div>
                              ) : null}
                            </Col>
                            <Col lg={6}>
                              <NumericFormat
                                className="form-control"
                                value={
                                  formikReview?.values?.reviewDelayRange?.end
                                }
                                placeholder={t("Review Delay Range")}
                                allowLeadingZeros
                                thousandSeparator=","
                                onValueChange={(values: any) => {
                                  const { floatValue } = values;
                                  formikReview.setFieldValue(
                                    "reviewDelayRange.end",
                                    floatValue
                                  );
                                }}
                              />
                              {formikReview?.touched?.reviewDelayRange?.end &&
                                formikReview?.errors?.reviewDelayRange?.end ? (
                                <div className="text-danger mt-2">
                                  {formikReview?.errors?.reviewDelayRange?.end}
                                </div>
                              ) : null}
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="maxPerDay" className="form-label">
                            {t("Max Reviews per Day")}{" "}
                          </Label>
                          <NumericFormat
                            className="form-control"
                            value={formikReview?.values?.maxPerDay}
                            placeholder={t("Max Reviews per Day")}
                            allowLeadingZeros
                            thousandSeparator=","
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              formikReview.setFieldValue(
                                "maxPerDay",
                                floatValue
                              );
                            }}
                          />
                          {formikReview?.touched?.maxPerDay &&
                            formikReview?.errors?.maxPerDay ? (
                            <div className="text-danger mt-2">
                              {formikReview?.errors?.maxPerDay}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="maxPerRun" className="form-label">
                            {t("Max Reviews per Run")}{" "}
                          </Label>
                          <NumericFormat
                            className="form-control"
                            value={formikReview?.values?.maxPerRun}
                            placeholder={t("Max Reviews per Run")}
                            allowLeadingZeros
                            thousandSeparator=","
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              formikReview.setFieldValue(
                                "maxPerRun",
                                floatValue
                              );
                            }}
                          />
                          {formikReview?.touched?.maxPerRun &&
                            formikReview?.errors?.maxPerRun ? (
                            <div className="text-danger mt-2">
                              {formikReview?.errors?.maxPerRun}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="mb-3">
                          <Label htmlFor="contactEmail" className="form-label">
                            {t("Category Prompts")}{" "}
                            <span className="text-danger">*</span>
                          </Label>

                          <div>
                            <FormikProvider value={formikReview}>
                              <FieldArray
                                name="categoryPrompts"
                                render={({ push }) => (
                                  <>
                                    <Collapse
                                      activeKey={activeReviewKeys}
                                      onChange={(keys) =>
                                        setActiveReviewKeys(
                                          Array.isArray(keys)
                                            ? keys.map(Number)
                                            : []
                                        )
                                      }
                                      collapsible="icon"
                                      items={formikReview?.values?.categoryPrompts?.map(
                                        (prompt, index) => ({
                                          key: index,
                                          label: (
                                            <div className="">
                                              <Input
                                                name={`categoryPrompts.${index}.category`}
                                                value={prompt.category ?? ""}
                                                placeholder={`${t(
                                                  "Enter category"
                                                )}...`}
                                                onChange={
                                                  formikReview.handleChange
                                                }
                                              />
                                              {index > 0 && (
                                                <small
                                                  style={{
                                                    right: 4,
                                                    top: "calc(50% - 11px)",
                                                    fontSize: 14,
                                                  }}
                                                  className="text-danger position-absolute cursor-pointer"
                                                  onClick={() => {
                                                    setIsOpenConfirm(true);
                                                    setIndexDelete(index);
                                                    setFormRemove(formikReview);
                                                  }}
                                                >
                                                  <i className="ri-delete-bin-3-fill align-bottom"></i>
                                                </small>
                                              )}
                                            </div>
                                          ),
                                          children: (
                                            <TextArea
                                              name={`categoryPrompts.${index}.prompt`}
                                              value={prompt.prompt ?? ""}
                                              autoSize={{ minRows: 6 }}
                                              placeholder={`${t(
                                                "Enter prompt"
                                              )}...`}
                                              onChange={
                                                formikReview.handleChange
                                              }
                                            />
                                          ),
                                        })
                                      )}
                                    />

                                    <button
                                      className="btn btn-dark fs-14 mt-4"
                                      type="button"
                                      style={{ width: "140px" }}
                                      onClick={() => {
                                        const newIndex =
                                          formikReview.values.categoryPrompts
                                            .length;
                                        push(DEFAULT_CATEGORY_PROMPTS);
                                        setActiveReviewKeys((prev) => [
                                          ...prev,
                                          newIndex,
                                        ]);
                                      }}
                                    >
                                      {t("Add Prompt")}
                                    </button>
                                  </>
                                )}
                              />
                            </FormikProvider>
                          </div>
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="hstack gap-2 justify-content-end my-4">
                          <button
                            className="btn btn-primary fs-14"
                            color="success"
                            type="submit"
                            disabled={isLoading}
                            style={{ width: "140px" }}
                          >
                            {isLoading ? (
                              <Spinner size="sm" className="me-2"></Spinner>
                            ) : (
                              <>{t("Update")}</>
                            )}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </form>
                )}
              </TabPane>
            </TabContent>
          </Card>
          <ToastContainer closeButton={false} limit={1} />
        </Container>

        <ModalConfirm
          header={t("Delete this prompt")}
          isOpen={isOpenConfirm}
          isLoading={isConfirmLoading}
          onClose={onCloseConfirmClick}
          onConfirm={handleActionDelete}
        />
      </div>
    </React.Fragment>
  );
};

export default AISetting;
